import get from 'lodash/get';
import flatten from 'lodash/flatten';
import forEach from 'lodash/forEach';
import unset from 'lodash/unset';
import cloneDeep from 'lodash/cloneDeep';

import { flattenObject } from 'utils/objectFlatter';
import { isFormBasedTrigger } from '@xmatters/common/constants/scriptSteps';

const allFormTriggerProperties = [
  'recipients',
  'handling.priority',
  'handling.expirationInMinutes',
  'handling.overrideDeviceRestrictions',
  'handling.escalationOverride',
  'handling.bypassPhoneIntro',
  'handling.requirePhonePassword',
  'handling.voicemailOptions.retries',
  'handling.voicemailOptions.interval',
  'handling.voicemailOptions.type',
  'senderOverrides.displayName',
  'senderOverrides.callerID',
  'targetDeviceNames',
  'incident.summary',
  'incident.description',
  'incident.severity',
  'incident.reporter',
  'automation.incidentId',
  'automation.summary',
  'automation.description',
  'automation.severity',
  'automation.severityLevel',
  'automation.status',
];

// All/Other Trigger Outputs Map
export const triggerPropertiesMap = {
  STATUS: {
    rootPropNode: 'statusChanged',
    eventPath: 'event',
    props: [
      'statusChanged.auditType',
      'statusChanged.at',
      'statusChanged.by.id',
      'statusChanged.by.targetName',
      'statusChanged.by.firstName',
      'statusChanged.by.lastName',
    ],
  },
  DELIVERYSTATUS: {
    rootPropNode: 'deliveryStatusUpdated',
    callbackNode: 'notification',
    eventPath: 'notification.event',
    props: [
      'notification.deliveryStatus',
      'notification.recipient.alertContext',
      'notification.recipient.id',
      'notification.recipient.name',
      'notification.recipient.targetName',
      'notification.recipient.owner.id',
      'notification.recipient.owner.firstName',
      'notification.recipient.owner.lastName',
      'notification.recipient.owner.targetName',
      'deliveryStatusUpdated.at',
      'deliveryStatusUpdated.auditType',
      'deliveryStatusUpdated.deliveryStatusType',
      'deliveryStatusUpdated.deliveryStatusMessage',
    ],
  },
  RESPONSE: {
    rootPropNode: 'respondedTo',
    eventPath: 'response.event',
    props: [
      'response.response',
      'response.deviceName',
      'respondedTo.at',
      'respondedTo.by.alertContext',
      'respondedTo.by.targetName',
      'respondedTo.by.id',
      'respondedTo.by.firstName',
      'respondedTo.by.lastName',
      'respondedTo.auditType',
    ],
  },
  ESCALATION: {
    rootPropNode: 'escalated',
    callbackNode: 'escalation',
    eventPath: 'escalation.event',
    props: [
      'escalation.reason',
      'escalation.escalationType',
      'escalated.at',
      'escalated.by.id',
      'escalated.by.targetName',
      'escalated.by.firstName',
      'escalated.by.lastName',
      'escalation.group.id',
      'escalation.group.targetName',
      'escalation.group.status',
      'escalation.from',
      'escalation.to',
    ],
  },
  ANNOTATION: {
    rootPropNode: 'annotated',
    callbackNode: 'annotation',
    eventPath: 'annotation.event',
    props: [
      'annotation.comment',
      'annotated.at',
      'annotation.author.targetName',
      'annotation.author.id',
      'annotation.author.firstName',
      'annotation.author.lastName',
      'annotation.response.response',
    ],
  },
  TARGETEDRECIPIENTFAILURE: {
    rootPropNode: 'targetingFailed',
    callbackNode: 'targeted',
    eventPath: 'targeted.event',
    props: [
      'targeted.triggerType',
      'targetingFailed.at',
      'targeted.totalRecipients',
      'targeted.recipients',
    ],
  },
  INBOUND_EMAIL: {
    rootPropNode: 'email',
    callbackNode: 'email',
    eventPath: 'email',
    props: [
      'email.to',
      'email.sender',
      'email.subject',
      'email.htmlBody',
      'email.plaintextBody',
      'email.headers',
      'email.senderIp',
      'email.recipients',
    ],
  },
  /**
   * FORM_TRIGGER
   * Several of these properties are filtered out
   * a couple of new properties are also appended
   * depending on the integration form
   */
  FORM_TRIGGER: {
    rootPropNode: 'form',
    callbackNode: 'form',
    eventPath: 'form',
    props: allFormTriggerProperties, // These are special for form_triggers
  },
};

export const propertyToTriggerTypeMap = {};
forEach(triggerPropertiesMap, (trigger, triggerType) => {
  trigger.props.forEach(prop => {
    propertyToTriggerTypeMap[prop] = triggerType;
  });
});

export const sectionType = {
  INCIDENT: 'INCIDENT',
  RECIPIENTS: 'RECIPIENTS',
  HANDLING_OPTIONS: 'HANDLING_OPTIONS',
  SENDER_OVERRIDES: 'SENDER_OVERRIDES',
  DEVICE_FILTER: 'DEVICE_FILTER',
  CUSTOM_SECTION: 'CUSTOM_SECTION',
  CONFERENCE_BRIDGE: 'CONFERENCE_BRIDGE',
  AUTOMATION: 'AUTOMATION',
  INCIDENT_TYPE_PROPERTIES: 'INCIDENT_TYPE_PROPERTIES',
};

const handlingProps = allFormTriggerProperties.filter(key =>
  key.startsWith('handling'),
);
const senderOverProps = allFormTriggerProperties.filter(key =>
  key.startsWith('senderOverrides'),
);
const incidentProps = allFormTriggerProperties.filter(key =>
  key.startsWith('incident'),
);
const automationProps = allFormTriggerProperties.filter(key =>
  key.startsWith('automation'),
);

const getPropsForSection = {
  [sectionType.AUTOMATION]: () => [...automationProps],
  [sectionType.INCIDENT]: () => [...incidentProps],
  [sectionType.RECIPIENTS]: () => ['recipients'],
  [sectionType.HANDLING_OPTIONS]: () => [...handlingProps],
  [sectionType.SENDER_OVERRIDES]: () => [...senderOverProps],
  [sectionType.DEVICE_FILTER]: () => ['targetDeviceNames'],
  [sectionType.CONFERENCE_BRIDGE]: () => [], // Knowingly not hooked up for now...
  [sectionType.CUSTOM_SECTION]: section => {
    // Return the individual properties themselves
    return Array.isArray(section.items)
      ? section.items.map(
          property => `properties.${property.propertyDefinition.name}`,
        )
      : [];
  },
  [sectionType.INCIDENT_TYPE_PROPERTIES]: section => {
    // Return the individual properties themselves
    return Array.isArray(section.items)
      ? section.items.map(
          property => `properties.${property.propertyDefinition.name}`,
        )
      : [];
  },
};

/**
 * Return only those values defined by the trigger type
 * @param triggeredBy the triggered by type, such as TARGETEDRECIPIENTFAILURE.
 * @param values the actual property name/value pairs.
 * @param matchStartOnly
 */
function filter(triggeredBy, values, matchStartOnly) {
  const dropArray = k => k.split('[')[0];

  const getKeyName = (k, rootNode, callbackNode) =>
    k.startsWith(callbackNode) || k.startsWith(rootNode)
      ? k
      : `${rootNode}.${k}`;

  // add prefix
  const rootName = triggerPropertiesMap[triggeredBy].rootPropNode;
  const { callbackNode } = triggerPropertiesMap[triggeredBy];
  const updated = Object.keys(values).reduce((acc, k) => {
    acc[getKeyName(k, rootName, callbackNode)] = values[k];
    return acc;
  }, {});

  const result = {};
  const defined = triggerPropertiesMap[triggeredBy].props;
  defined.forEach(p => {
    Object.entries(updated)
      .filter(e =>
        matchStartOnly ? dropArray(e[0]).startsWith(p) : dropArray(e[0]) === p,
      )
      .forEach(e => {
        result[e[0]] = e[1];
        return null;
      });
    return null;
  });
  return result;
}

/**
 * Returns event and other trigger outputs separately
 */
export function parseTriggerOutputsJsonInput(requestBody, triggeredBy) {
  if (!requestBody || !triggeredBy || !triggerPropertiesMap[triggeredBy]) {
    return {};
  }

  const cloned = cloneDeep(requestBody);
  const rootNode = triggerPropertiesMap[triggeredBy].rootPropNode;
  const outputObj = get(cloned, rootNode);
  if (triggeredBy === 'INBOUND_EMAIL') {
    const outputs = filter(triggeredBy, flattenObject(outputObj), true);
    return {
      outputs,
    };
  }
  // remove event
  unset(outputObj, triggerPropertiesMap[triggeredBy].eventPath);
  const eventObj = get(
    requestBody,
    `${triggerPropertiesMap[triggeredBy].rootPropNode}.${triggerPropertiesMap[triggeredBy].eventPath}`,
  );
  const event = flattenObject(eventObj);
  const outputs = filter(triggeredBy, flattenObject(outputObj));
  return {
    outputs,
    event,
  };
}

/**
 * Returns event and other trigger outputs separately
 */
export function parseTriggerOutputs(requestBody, triggeredBy) {
  if (!requestBody) {
    return {};
  }
  try {
    return parseTriggerOutputsJsonInput(JSON.parse(requestBody), triggeredBy);
  } catch (e) {
    return {};
  }
}

export function getTriggerPropertiesMap() {
  return Object.keys(triggerPropertiesMap).reduce((acc, k) => {
    acc[k] = triggerPropertiesMap[k].props;
    return acc;
  }, {});
}

// Form trigger will have different sections depending on the formSections associated
export const getFormTriggerProperties = (formTrigger, formSections) => {
  if (!isFormBasedTrigger(formTrigger.type)) {
    return []; // or we could getTrigger below?
  }
  const finalSections = formSections.map(section =>
    // Get all the props for each of the form sections
    getPropsForSection[section.type](section),
  );
  return flatten(finalSections);
};

// ORIGINAL getTriggerProperties, but now we wrap just below...
export const getOtherTriggerProperties = trigger => {
  const { triggeredBy, type } = trigger;
  const data = triggerPropertiesMap[triggeredBy || type];
  return data ? [...data.props] : [];
};

export const getTriggerProperties = (trigger, formSections = []) => {
  return isFormBasedTrigger(trigger.type)
    ? getFormTriggerProperties(trigger, formSections)
    : getOtherTriggerProperties(trigger);
};

export const getTriggerPropertyDisplayName = (id, trigger) => {
  const { triggeredBy, type } = trigger;
  const data = triggerPropertiesMap[triggeredBy || type];
  return data && data.displayName ? data.displayName[id] : id;
};

export const automationOutput = {
  title: 'Automation',
  type: 'AUTOMATION',
};
